import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import StatCard from '../../components/common/StatCard';
import DonutChart from '../../components/common/DonutChart';
import BarChartCard from '../../components/common/BarChartCard';
import LineChartCard from '../../components/common/LineChartCard';
import FollowUpsTable from '../../components/common/FollowUpsTable';
import TrialBanner from '../../components/TrialBanner';
import UpcomingReminders from '../../components/common/UpcomingReminders';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { Users, Flame, MapPin, Trophy } from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      if (!user) return;
      try {
        const response = await api(`/dashboard/stats`);
        if (response.ok) {
          const stats = await response.json();
          setData(stats);
        }
      } catch (err) {
        console.error("Dashboard error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [user]);

  if (loading) return <MainLayout title="Dashboard"><div style={{ padding: '40px', textAlign: 'center' }}>Loading Dashboard...</div></MainLayout>;
  if (!data) return <MainLayout title="Dashboard"><div style={{ padding: '40px', textAlign: 'center' }}>Error loading data</div></MainLayout>;

  // Format donut data
  const donutData = data.statusBreakdown.map(sb => ({
    name: sb._id || 'New',
    value: sb.count
  }));

  // Format bar chart data
  const barData = data.sourceBreakdown.map(sb => ({
    _id: sb._id || 'Direct',
    count: sb.count
  }));

  // Format team performance data
  const teamData = data.teamPerformance || [];
  
  // Get upcoming reminders
  const upcomingReminders = data.upcomingReminders || [];

  return (
    <MainLayout title="Dashboard">
      <TrialBanner />
      
      {/* Header section */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--color-text-primary)', marginBottom: '4px' }}>
            Good morning, {user.name.split(' ')[0]} 👋
          </h1>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '14px' }}>
            Here's your business overview for {new Date().toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '24px' }}>
        <StatCard 
          label="Total Leads" 
          value={data.stats.totalLeads} 
          subtext="Total in system" 
          icon={Users}
          gradient="var(--gradient-blue)"
        />
        <StatCard 
          label="Hot Leads" 
          value={data.stats.hotLeads} 
          icon={Flame}
          iconColor="var(--chart-orange)"
          color="var(--chart-orange)"
          gradient="var(--gradient-orange)"
        />
        <StatCard 
          label="Closed Won" 
          value={data.stats.closedWon} 
          icon={Trophy}
          iconColor="var(--chart-green)"
          color="var(--chart-green)"
          gradient="var(--gradient-green)"
        />
      </div>

      {/* Charts Row 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        <div className="card" style={{ 
          background: 'var(--color-background-primary)', 
          padding: '24px', 
          borderRadius: 'var(--border-radius-xl)',
          border: '1px solid var(--color-border-tertiary)',
          boxShadow: 'var(--shadow-sm)'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '20px' }}>Lead Status Breakdown</h3>
          <DonutChart data={donutData} />
        </div>
        <BarChartCard title="Top Lead Sources" data={barData} />
      </div>

      {/* Charts Row 2 */}
      {data.monthlyTrend && data.monthlyTrend.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <LineChartCard title="Monthly Lead Trend (Last 6 Months)" data={data.monthlyTrend} />
        </div>
      )}

      {/* Tables Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        <FollowUpsTable 
          followups={data.todayFollowups} 
          overdueCount={data.overdueCount} 
          onViewAll={() => navigate(user.role === 'super-admin' ? '/admin/leads' : `/${user.role}/leads`)}
          onNavigate={(path) => navigate(path)}
        />
        
        <UpcomingReminders reminders={upcomingReminders} />
        
        {/* Team Performance Mini Table */}
        <div className="card" style={{ 
          background: 'var(--color-background-primary)', 
          padding: '24px', 
          borderRadius: 'var(--border-radius-xl)',
          border: '1px solid var(--color-border-tertiary)',
          boxShadow: 'var(--shadow-sm)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '20px' }}>Top Performers</h3>
          {teamData && teamData.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {teamData.slice(0, 5).map((member, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ 
                      width: '32px', height: '32px', borderRadius: '50%', 
                      background: 'var(--color-background-info)', color: 'var(--color-brand-primary)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 'bold', fontSize: '12px'
                    }}>
                      {member.name.substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '600' }}>{member.name}</div>
                      <div style={{ fontSize: '11px', color: 'var(--color-text-tertiary)', textTransform: 'capitalize' }}>{member.role}</div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--chart-green)' }}>{member.closed} Won</div>
                    <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>{member.leads} Leads</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-tertiary)', fontSize: '13px' }}>
              No team data available
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default Dashboard;
